import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MODULES } from "@/data";
import { useApply } from "@/context";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

function ModuleCard({ mod, index }) {
  const { openApply } = useApply();
  return (
    <motion.article
      custom={index}
      variants={reveal}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      data-testid={`module-card-${mod.id}`}
      id={mod.id}
      className={`group relative flex scroll-mt-28 flex-col overflow-hidden rounded-3xl border border-gray-200/70 bg-pure-white shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-deep-trust-blue/15 ${mod.span}`}
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={mod.image}
          alt={mod.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-deep-trust-blue backdrop-blur">
          {mod.tag}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-7">
        <h3 className="font-heading text-2xl font-bold tracking-tight text-deep-trust-blue">
          {mod.title}
        </h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-deep-trust-blue/65">{mod.desc}</p>
        <button
          data-testid={`module-apply-${mod.id}`}
          onClick={() => openApply(mod.title)}
          className="mt-6 inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-vibrant-orange transition-colors hover:text-orange-600"
        >
          Enroll in this module
          <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </div>
    </motion.article>
  );
}

export default function Modules() {
  return (
    <section id="modules" data-testid="modules-section" className="bg-light-gray py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="mb-14 max-w-2xl">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-green">
            Five Pathways
          </span>
          <h2 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-deep-trust-blue sm:text-5xl">
            Modules that turn ambition into income.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-deep-trust-blue/65 md:text-lg">
            Every course is taught voice-first, project-based, and backed by a placement promise —
            designed for real livelihoods, not just certificates.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {MODULES.map((mod, i) => (
            <ModuleCard key={mod.id} mod={mod} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
